import { useState, useEffect } from "react";
import {
  ShoppingCart,
  Package,
  DollarSign,
  Users,
  FileText,
  Mail,
  Calculator,
  Menu,
  ChevronRight,
  Plus,
  Search,
  Bell,
  User,
  TrendingUp,
  Clock,
  AlertCircle,
  Check,
  X,
  Edit,
  Trash2,
  Eye,
  Download,
  Filter,
  Settings,
  LogOut,
  ChefHat,
  Globe,
  BarChart3,
  Utensils,
  Coffee,
  Pizza,
} from "lucide-react";

// Configuración API Odoo
const ODOO_API_CONFIG = {
  baseURL: "http://localhost:8069",
  database: "restaurant_db",
  version: "18.0",
};

// Servicio API Base
const odooAPI = {
  authenticate: async (login: string, password: string) => {
    const response = await fetch(
      `${ODOO_API_CONFIG.baseURL}/web/session/authenticate`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          jsonrpc: "2.0",
          params: {
            db: ODOO_API_CONFIG.database,
            login,
            password,
          },
        }),
      },
    );
    return response.json();
  },

  call: async (
    model: string,
    method: string,
    args: any[] = [],
    kwargs: any = {},
  ) => {
    const session_id = localStorage.getItem("session_id");
    const response = await fetch(
      `${ODOO_API_CONFIG.baseURL}/web/dataset/call_kw`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Openerp-Session-Id": session_id || "",
        },
        body: JSON.stringify({
          jsonrpc: "2.0",
          method: "call",
          params: {
            model,
            method,
            args,
            kwargs,
          },
        }),
      },
    );
    return response.json();
  },
};

const ByteRestaurantSystem = () => {
  const [activeModule, setActiveModule] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [notifications] = useState(3);
  const [user] = useState({ name: "Admin Usuario", role: "Administrador" });

  // Módulos del sistema con iconos actualizados
  const modules = [
    {
      id: "dashboard",
      name: "Dashboard",
      icon: BarChart3,
      color: "from-purple-500 to-purple-600",
    },
    {
      id: "menu-digital",
      name: "Menú Digital",
      icon: Utensils,
      color: "from-purple-600 to-purple-700",
    },
    {
      id: "pos",
      name: "Punto de Venta",
      icon: ShoppingCart,
      color: "from-purple-500 to-pink-500",
    },
    {
      id: "inventory",
      name: "Inventarios",
      icon: Package,
      color: "from-purple-600 to-indigo-600",
    },
    {
      id: "sales",
      name: "Ventas",
      icon: TrendingUp,
      color: "from-purple-500 to-purple-700",
    },
    {
      id: "purchases",
      name: "Compras",
      icon: DollarSign,
      color: "from-indigo-500 to-purple-600",
    },
    {
      id: "accounting",
      name: "Contabilidad",
      icon: Calculator,
      color: "from-purple-600 to-purple-800",
    },
    {
      id: "employees",
      name: "Empleados",
      icon: Users,
      color: "from-purple-500 to-indigo-500",
    },
    {
      id: "website",
      name: "Página Web",
      icon: Globe,
      color: "from-purple-600 to-pink-600",
    },
    {
      id: "email",
      name: "Email Marketing",
      icon: Mail,
      color: "from-indigo-600 to-purple-600",
    },
  ];

  // API Services para cada módulo
  const apiServices = {
    menuDigital: {
      getProducts: () => odooAPI.call("byte.menu.product", "search_read", [[]]),
      createProduct: (data: any) =>
        odooAPI.call("byte.menu.product", "create", [data]),
      updateProduct: (id: number, data: any) =>
        odooAPI.call("byte.menu.product", "write", [[id], data]),
      deleteProduct: (id: number) =>
        odooAPI.call("byte.menu.product", "unlink", [[id]]),
    },
    pos: {
      createOrder: (data: any) => odooAPI.call("pos.order", "create", [data]),
      getOrders: () => odooAPI.call("pos.order", "search_read", [[]]),
      processPayment: (orderId: number, paymentData: any) =>
        odooAPI.call("pos.order", "process_payment", [[orderId], paymentData]),
    },
    inventory: {
      getStock: () => odooAPI.call("stock.quant", "search_read", [[]]),
      updateStock: (id: number, qty: number) =>
        odooAPI.call("stock.quant", "write", [[id], { quantity: qty }]),
      getLowStock: () =>
        odooAPI.call("stock.quant", "get_low_stock_products", []),
    },
  };

  // Componente Dashboard mejorado
  const Dashboard = () => {
    const [stats, setStats] = useState({
      dailySales: 45230,
      ordersToday: 156,
      activeCustomers: 89,
      lowStock: 12,
    });

    useEffect(() => {
      // Cargar estadísticas desde Odoo
      const loadStats = async () => {
        try {
          const salesData = await odooAPI.call(
            "byte.dashboard",
            "get_daily_stats",
            [],
          );
          if (salesData.result) {
            setStats(salesData.result);
          }
        } catch (error) {
          console.error("Error loading dashboard stats:", error);
        }
      };
      loadStats();
    }, []);

    return (
      <div className="space-y-6">
        {/* KPIs con gradientes morados */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-purple-100">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-3 rounded-xl">
                <DollarSign className="h-6 w-6 text-white" />
              </div>
              <span className="text-green-500 text-sm font-medium bg-green-50 px-2 py-1 rounded-full">
                +12%
              </span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">
              {new Intl.NumberFormat("es-CO", {
                style: "currency",
                currency: "COP",
                minimumFractionDigits: 0,
              }).format(stats.dailySales * 4500)}
            </h3>
            <p className="text-gray-500 text-sm mt-1">Ventas del día</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-purple-100">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-gradient-to-br from-purple-600 to-pink-500 p-3 rounded-xl">
                <ShoppingCart className="h-6 w-6 text-white" />
              </div>
              <span className="text-green-500 text-sm font-medium bg-green-50 px-2 py-1 rounded-full">
                +8%
              </span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">
              {stats.ordersToday}
            </h3>
            <p className="text-gray-500 text-sm mt-1">Órdenes hoy</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-purple-100">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-3 rounded-xl">
                <Users className="h-6 w-6 text-white" />
              </div>
              <span className="text-red-500 text-sm font-medium bg-red-50 px-2 py-1 rounded-full">
                -2%
              </span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">
              {stats.activeCustomers}
            </h3>
            <p className="text-gray-500 text-sm mt-1">Clientes activos</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-purple-100">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-gradient-to-br from-purple-500 to-purple-700 p-3 rounded-xl">
                <Package className="h-6 w-6 text-white" />
              </div>
              <span className="text-yellow-500 text-sm font-medium bg-yellow-50 px-2 py-1 rounded-full flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Alerta
              </span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">
              {stats.lowStock}
            </h3>
            <p className="text-gray-500 text-sm mt-1">Productos bajo stock</p>
          </div>
        </div>

        {/* Gráficos y tablas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Ventas de la semana
            </h3>
            <div className="h-64 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4">
              <div className="h-full flex items-end justify-between gap-2">
                {["Lun", "Mar", "Mie", "Jue", "Vie", "Sab", "Dom"].map(
                  (day) => {
                    const height = Math.random() * 80 + 20;
                    const sales = Math.floor(Math.random() * 150000) + 50000;
                    return (
                      <div
                        key={day}
                        className="flex flex-col items-center flex-1"
                      >
                        <div className="text-xs text-gray-600 mb-1">
                          {new Intl.NumberFormat("es-CO", {
                            style: "currency",
                            currency: "COP",
                            minimumFractionDigits: 0,
                          }).format(sales)}
                        </div>
                        <div
                          className="w-full bg-gradient-to-t from-purple-500 to-purple-400 rounded-t-lg transition-all duration-300 hover:from-purple-600 hover:to-purple-500"
                          style={{ height: `${height}%` }}
                        ></div>
                        <div className="text-xs font-medium text-gray-700 mt-2">
                          {day}
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Productos más vendidos
            </h3>
            <div className="space-y-3">
              {[
                { name: "Sashimi Premium", qty: 45, icon: "🍣", price: 45000 },
                { name: "Dragon Roll", qty: 37, icon: "🐉", price: 42000 },
                { name: "Maki California", qty: 29, icon: "🥑", price: 28000 },
                { name: "Nigiri Surtido", qty: 21, icon: "🍱", price: 38000 },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 hover:bg-purple-50 rounded-xl transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{item.icon}</span>
                    <div>
                      <span className="text-gray-700 font-medium block">
                        {item.name}
                      </span>
                      <span className="text-purple-600 text-sm font-semibold">
                        {new Intl.NumberFormat("es-CO", {
                          style: "currency",
                          currency: "COP",
                          minimumFractionDigits: 0,
                        }).format(item.price)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">
                      {item.qty}
                    </span>
                    <span className="text-gray-500 text-sm">unidades</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Gráficos adicionales de análisis de ventas */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Gráfico de dona - Ventas por categoría */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Ventas por Categoría
            </h3>
            <div className="h-48 flex items-center justify-center">
              <div className="relative w-32 h-32">
                {/* Simulación de gráfico de dona */}
                <div className="absolute inset-0 rounded-full border-8 border-purple-200"></div>
                <div className="absolute inset-0 rounded-full border-8 border-purple-500 border-t-transparent transform rotate-45"></div>
                <div className="absolute inset-2 rounded-full border-8 border-indigo-500 border-t-transparent border-r-transparent transform rotate-180"></div>
                <div className="absolute inset-4 rounded-full border-8 border-pink-500 border-t-transparent border-r-transparent border-b-transparent transform rotate-270"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-lg font-bold text-gray-900">67%</div>
                    <div className="text-xs text-gray-500">Principales</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Principales</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">67%</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-indigo-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Entradas</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">23%</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-pink-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Bebidas</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">10%</span>
              </div>
            </div>
          </div>

          {/* Gráfico de líneas - Tendencia mensual */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Tendencia Mensual
            </h3>
            <div className="h-48 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4">
              <div className="h-full relative">
                <svg className="w-full h-full" viewBox="0 0 300 160">
                  <defs>
                    <linearGradient
                      id="gradient"
                      x1="0%"
                      y1="0%"
                      x2="0%"
                      y2="100%"
                    >
                      <stop
                        offset="0%"
                        style={{ stopColor: "#8B5CF6", stopOpacity: 0.8 }}
                      />
                      <stop
                        offset="100%"
                        style={{ stopColor: "#8B5CF6", stopOpacity: 0.1 }}
                      />
                    </linearGradient>
                  </defs>
                  <path
                    d="M 20 140 Q 60 120 100 100 T 180 80 T 260 60"
                    stroke="#8B5CF6"
                    strokeWidth="3"
                    fill="none"
                    strokeLinecap="round"
                  />
                  <path
                    d="M 20 140 Q 60 120 100 100 T 180 80 T 260 60 L 260 140 L 20 140 Z"
                    fill="url(#gradient)"
                  />
                  {/* Puntos de datos */}
                  {[20, 100, 180, 260].map((x, i) => (
                    <circle
                      key={i}
                      cx={x}
                      cy={140 - i * 20 - 40}
                      r="4"
                      fill="#8B5CF6"
                      stroke="white"
                      strokeWidth="2"
                    />
                  ))}
                </svg>
              </div>
            </div>
            <div className="mt-4 flex justify-between text-xs text-gray-600">
              <span>Ene</span>
              <span>Feb</span>
              <span>Mar</span>
              <span>Abr</span>
            </div>
          </div>

          {/* Métricas de rendimiento */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Métricas de Rendimiento
            </h3>
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-green-800">
                    Ticket Promedio
                  </span>
                  <TrendingUp className="h-4 w-4 text-green-600" />
                </div>
                <div className="text-2xl font-bold text-green-900">
                  {new Intl.NumberFormat("es-CO", {
                    style: "currency",
                    currency: "COP",
                    minimumFractionDigits: 0,
                  }).format(32500)}
                </div>
                <div className="text-xs text-green-700">
                  +8% vs mes anterior
                </div>
              </div>

              <div className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-800">
                    Conversión
                  </span>
                  <BarChart3 className="h-4 w-4 text-blue-600" />
                </div>
                <div className="text-2xl font-bold text-blue-900">68%</div>
                <div className="text-xs text-blue-700">
                  De visitantes a pedidos
                </div>
              </div>

              <div className="p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-purple-800">
                    Tiempo Promedio
                  </span>
                  <Clock className="h-4 w-4 text-purple-600" />
                </div>
                <div className="text-2xl font-bold text-purple-900">18 min</div>
                <div className="text-xs text-purple-700">
                  De pedido a entrega
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Componente Menú Digital con API
  const MenuDigital = () => {
    const [selectedCategory, setSelectedCategory] = useState("entradas");
    const [loading, setLoading] = useState(false);

    const categories = [
      { id: "entradas", name: "Entradas", icon: Coffee },
      { id: "principales", name: "Platos Principales", icon: ChefHat },
      { id: "postres", name: "Postres", icon: Pizza },
      { id: "bebidas", name: "Bebidas", icon: Coffee },
    ];

    // Productos de sushi con imágenes y precios en COP
    const sushiProducts = {
      entradas: [
        {
          id: 1,
          name: "Gyozas de Cerdo",
          description: "Dumplings fritos rellenos de cerdo y vegetales",
          price: 18000,
          image:
            "https://images.unsplash.com/photo-1496116218417-1a781b1c416c?w=400&h=300&fit=crop",
          category: "entradas",
        },
        {
          id: 2,
          name: "Edamame",
          description: "Vainas de soja cocidas al vapor con sal de mar",
          price: 12000,
          image:
            "https://images.unsplash.com/photo-1575262599410-837a72005862?q=80&w=879&auto=format&fit=crop",
          category: "entradas",
        },
        {
          id: 3,
          name: "Tempura de Camarón",
          description: "Camarones rebozados en tempura crujiente",
          price: 24000,
          image:
            "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=400&h=300&fit=crop",
          category: "entradas",
        },
        {
          id: 4,
          name: "Takoyaki",
          description: "Bolas de masa rellenas de pulpo con salsa especial",
          price: 16000,
          image:
            "https://images.unsplash.com/photo-1589302168068-964664d93dc0?w=400&h=300&fit=crop",
          category: "entradas",
        },
      ],
      principales: [
        {
          id: 5,
          name: "Sashimi Premium",
          description: "Selección de pescados frescos: salmón, atún, pargo",
          price: 45000,
          image:
            "https://images.unsplash.com/photo-1553621042-f6e147245754?w=400&h=300&fit=crop",
          category: "principales",
        },
        {
          id: 6,
          name: "Nigiri Surtido",
          description: "10 piezas de nigiri con pescados variados",
          price: 38000,
          image:
            "https://images.unsplash.com/photo-1563612116625-3012372fccce?w=400&h=300&fit=crop",
          category: "principales",
        },
        {
          id: 7,
          name: "Maki California",
          description: "Rollo con cangrejo, aguacate y pepino",
          price: 28000,
          image:
            "https://images.unsplash.com/photo-1579584425555-c3ce17fd4351?w=400&h=300&fit=crop",
          category: "principales",
        },
        {
          id: 8,
          name: "Uramaki Philadelphia",
          description: "Rollo con salmón, queso crema y cebollín",
          price: 32000,
          image:
            "https://images.unsplash.com/photo-1617196034796-73dfa7b1fd56?w=400&h=300&fit=crop",
          category: "principales",
        },
        {
          id: 9,
          name: "Dragon Roll",
          description: "Rollo especial con anguila, aguacate y salsa teriyaki",
          price: 42000,
          image:
            "https://images.unsplash.com/photo-1559797620-bcd3c1b9c8c6?w=400&h=300&fit=crop",
          category: "principales",
        },
        {
          id: 10,
          name: "Chirashi Bowl",
          description: "Arroz sushi con variedad de pescados y vegetales",
          price: 35000,
          image:
            "https://images.unsplash.com/photo-1546833999-b9f581a1996d?w=400&h=300&fit=crop",
          category: "principales",
        },
      ],
      postres: [
        {
          id: 11,
          name: "Mochi de Té Verde",
          description: "Helado de té verde envuelto en masa de arroz",
          price: 8000,
          image:
            "https://images.unsplash.com/photo-1563805042-7684c019e1cb?w=400&h=300&fit=crop",
          category: "postres",
        },
        {
          id: 12,
          name: "Tempura de Helado",
          description: "Helado de vainilla rebozado en tempura",
          price: 12000,
          image:
            "https://images.unsplash.com/photo-1567206563064-6f60f40a2b57?w=400&h=300&fit=crop",
          category: "postres",
        },
      ],
      bebidas: [
        {
          id: 13,
          name: "Sake Premium",
          description: "Sake japonés servido caliente o frío",
          price: 25000,
          image:
            "https://images.unsplash.com/photo-1544145945-f90425340c7e?w=400&h=300&fit=crop",
          category: "bebidas",
        },
        {
          id: 14,
          name: "Té Verde",
          description: "Té verde japonés tradicional",
          price: 6000,
          image:
            "https://images.unsplash.com/photo-1556881286-3b3936c2e365?w=400&h=300&fit=crop",
          category: "bebidas",
        },
        {
          id: 15,
          name: "Ramune",
          description: "Bebida gaseosa japonesa sabor original",
          price: 8000,
          image:
            "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400&h=300&fit=crop",
          category: "bebidas",
        },
      ],
    };

    useEffect(() => {
      loadProducts();
    }, [selectedCategory]);

    const loadProducts = async () => {
      setLoading(true);
      try {
        const response = await apiServices.menuDigital.getProducts();
        if (response.result) {
          console.log(
            "Products loaded:",
            response.result.filter(
              (p: any) => p.category_id[1] === selectedCategory,
            ),
          );
        }
      } catch (error) {
        console.error("Error loading products:", error);
      }
      setLoading(false);
    };

    const formatPrice = (price: number) => {
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
      }).format(price);
    };

    const getCurrentProducts = () => {
      return (
        sushiProducts[selectedCategory as keyof typeof sushiProducts] || []
      );
    };

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Menú Digital</h2>
          <button className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-2.5 rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all duration-300 flex items-center gap-2 shadow-lg">
            <Plus className="h-4 w-4" />
            Nuevo Plato
          </button>
        </div>

        {/* Categorías mejoradas */}
        <div className="flex gap-6 border-b border-purple-100">
          {categories.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`pb-4 px-4 border-b-3 transition-all duration-300 flex items-center gap-2 ${
                  selectedCategory === cat.id
                    ? "border-purple-600 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium">{cat.name}</span>
              </button>
            );
          })}
        </div>

        {/* Grid de productos */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {getCurrentProducts().map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-purple-100 group"
              >
                <div className="h-48 relative overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-300"></div>
                </div>
                <div className="p-5">
                  <h3 className="font-semibold text-gray-900 text-lg">
                    {product.name}
                  </h3>
                  <p className="text-gray-500 text-sm mt-1 line-clamp-2">
                    {product.description}
                  </p>
                  <div className="flex justify-between items-center mt-4">
                    <span className="text-2xl font-bold text-purple-600">
                      {formatPrice(product.price)}
                    </span>
                    <div className="flex gap-2">
                      <button className="p-2 hover:bg-purple-50 rounded-lg transition-colors">
                        <Edit className="h-4 w-4 text-gray-600" />
                      </button>
                      <button className="p-2 hover:bg-purple-50 rounded-lg transition-colors">
                        <Eye className="h-4 w-4 text-gray-600" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Componente POS con integración Odoo
  const POS = () => {
    const [cart, setCart] = useState([
      {
        id: 1,
        name: "Sashimi Premium",
        price: 45000,
        quantity: 1,
        image:
          "https://images.unsplash.com/photo-1553621042-f6e147245754?w=400&h=300&fit=crop",
      },
      {
        id: 2,
        name: "Maki California",
        price: 28000,
        quantity: 2,
        image:
          "https://images.unsplash.com/photo-1579584425555-c3ce17fd4351?w=400&h=300&fit=crop",
      },
    ]);

    const [selectedCategory, setSelectedCategory] = useState("principales");

    // Productos de sushi para POS
    const sushiProducts = {
      entradas: [
        {
          id: 1,
          name: "Gyozas de Cerdo",
          price: 18000,
          image:
            "https://images.unsplash.com/photo-1496116218417-1a781b1c416c?w=400&h=300&fit=crop",
        },
        {
          id: 2,
          name: "Edamame",
          price: 12000,
          image:
            "https://images.unsplash.com/photo-1569059592440-3c4b66ee8244?w=400&h=300&fit=crop",
        },
        {
          id: 3,
          name: "Tempura de Camarón",
          price: 24000,
          image:
            "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=400&h=300&fit=crop",
        },
        {
          id: 4,
          name: "Takoyaki",
          price: 16000,
          image:
            "https://images.unsplash.com/photo-1589302168068-964664d93dc0?w=400&h=300&fit=crop",
        },
      ],
      principales: [
        {
          id: 5,
          name: "Sashimi Premium",
          price: 45000,
          image:
            "https://images.unsplash.com/photo-1553621042-f6e147245754?w=400&h=300&fit=crop",
        },
        {
          id: 6,
          name: "Nigiri Surtido",
          price: 38000,
          image:
            "https://images.unsplash.com/photo-1563612116625-3012372fccce?w=400&h=300&fit=crop",
        },
        {
          id: 7,
          name: "Maki California",
          price: 28000,
          image:
            "https://images.unsplash.com/photo-1579584425555-c3ce17fd4351?w=400&h=300&fit=crop",
        },
        {
          id: 8,
          name: "Dragon Roll",
          price: 42000,
          image:
            "https://images.unsplash.com/photo-1559797620-bcd3c1b9c8c6?w=400&h=300&fit=crop",
        },
      ],
      bebidas: [
        {
          id: 13,
          name: "Sake Premium",
          price: 25000,
          image:
            "https://images.unsplash.com/photo-1544145945-f90425340c7e?w=400&h=300&fit=crop",
        },
        {
          id: 14,
          name: "Té Verde",
          price: 6000,
          image:
            "https://images.unsplash.com/photo-1556881286-3b3936c2e365?w=400&h=300&fit=crop",
        },
        {
          id: 15,
          name: "Ramune",
          price: 8000,
          image:
            "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400&h=300&fit=crop",
        },
      ],
    };

    const total = cart.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0,
    );

    const formatPrice = (price: number) => {
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
      }).format(price);
    };

    const addToCart = (product: any) => {
      const existingItem = cart.find((item) => item.id === product.id);
      if (existingItem) {
        setCart(
          cart.map((item) =>
            item.id === product.id
              ? { ...item, quantity: item.quantity + 1 }
              : item,
          ),
        );
      } else {
        setCart([...cart, { ...product, quantity: 1 }]);
      }
    };

    const updateQuantity = (id: number, newQuantity: number) => {
      if (newQuantity <= 0) {
        setCart(cart.filter((item) => item.id !== id));
      } else {
        setCart(
          cart.map((item) =>
            item.id === id ? { ...item, quantity: newQuantity } : item,
          ),
        );
      }
    };

    const getCurrentProducts = () => {
      return (
        sushiProducts[selectedCategory as keyof typeof sushiProducts] || []
      );
    };

    const processOrder = async () => {
      try {
        const orderData = {
          lines: cart.map((item) => ({
            product_id: item.id,
            qty: item.quantity,
            price_unit: item.price,
          })),
          amount_total: total,
          partner_id: false, // Cliente anónimo
        };

        const response = await apiServices.pos.createOrder(orderData);
        if (response.result) {
          alert("Orden procesada correctamente");
          setCart([]);
        }
      } catch (error) {
        console.error("Error processing order:", error);
      }
    };

    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
        {/* Productos */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <div className="flex gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar productos..."
                  className="w-full pl-10 pr-4 py-3 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-3 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
              >
                <option value="principales">Platos Principales</option>
                <option value="entradas">Entradas</option>
                <option value="bebidas">Bebidas</option>
              </select>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {getCurrentProducts().map((product) => (
                <button
                  key={product.id}
                  onClick={() => addToCart(product)}
                  className="bg-gradient-to-br from-purple-50 to-indigo-50 hover:from-purple-100 hover:to-indigo-100 rounded-xl p-4 transition-all duration-300 border border-purple-200 hover:border-purple-300"
                >
                  <div className="h-20 rounded-lg mb-2 overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="text-sm font-medium text-gray-900 line-clamp-2">
                    {product.name}
                  </p>
                  <p className="text-lg font-bold text-purple-600">
                    {formatPrice(product.price)}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Carrito */}
        <div className="bg-white rounded-2xl shadow-lg border border-purple-100 flex flex-col h-full">
          <div className="p-6 border-b border-purple-100">
            <h3 className="text-lg font-semibold text-gray-900">
              Orden Actual
            </h3>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-4">
              {cart.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl"
                >
                  <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{item.name}</p>
                    <p className="text-sm text-gray-500">
                      {formatPrice(item.price)} x {item.quantity}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      className="p-2 hover:bg-purple-200 rounded-lg transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                    <span className="w-8 text-center font-medium">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      className="p-2 hover:bg-purple-200 rounded-lg transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="p-6 border-t border-purple-100 space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold text-gray-900">Total</span>
              <span className="text-3xl font-bold text-purple-600">
                {formatPrice(total)}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button className="bg-gradient-to-r from-red-500 to-red-600 text-white py-3 rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-300 font-medium">
                Cancelar
              </button>
              <button
                onClick={processOrder}
                className="bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-300 font-medium"
              >
                Pagar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Componente Inventarios con API
  const Inventory = () => {
    const [selectedTab, setSelectedTab] = useState("productos");

    useEffect(() => {
      loadInventory();
    }, []);

    const loadInventory = async () => {
      try {
        const response = await apiServices.inventory.getStock();
        if (response.result) {
          console.log("Inventory loaded:", response.result);
        }
      } catch (error) {
        console.error("Error loading inventory:", error);
      }
    };

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">
            Gestión de Inventarios
          </h2>
          <div className="flex gap-3">
            <button className="bg-white border border-purple-200 px-5 py-2.5 rounded-xl hover:bg-purple-50 transition-colors flex items-center gap-2">
              <Download className="h-4 w-4" />
              Exportar
            </button>
            <button className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-5 py-2.5 rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all duration-300 flex items-center gap-2 shadow-lg">
              <Plus className="h-4 w-4" />
              Nuevo Producto
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 border-b border-purple-100">
          {["Productos", "Categorías", "Movimientos", "Alertas"].map((tab) => (
            <button
              key={tab}
              onClick={() => setSelectedTab(tab.toLowerCase())}
              className={`pb-3 px-4 border-b-3 transition-all duration-300 font-medium ${
                selectedTab === tab.toLowerCase()
                  ? "border-purple-600 text-purple-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tabla de inventario */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-purple-100">
          <div className="p-4 border-b border-purple-100 flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar productos..."
                className="w-full pl-10 pr-4 py-2.5 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <button className="px-4 py-2.5 border border-purple-200 rounded-xl hover:bg-purple-50 flex items-center gap-2 transition-colors">
              <Filter className="h-4 w-4" />
              Filtros
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-purple-50 to-indigo-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Producto
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    SKU
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Stock
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Unidad
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Costo
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-purple-100">
                {[1, 2, 3, 4, 5].map((item) => (
                  <tr
                    key={item}
                    className="hover:bg-purple-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 bg-gradient-to-br from-purple-200 to-indigo-200 rounded-xl mr-3"></div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            Producto Byte {item}
                          </div>
                          <div className="text-sm text-gray-500">
                            Categoría Digital
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      BYTE00{item}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`text-sm font-medium ${
                          item === 1 ? "text-red-600" : "text-gray-900"
                        }`}
                      >
                        {item === 1 ? "5" : `${50 + item * 10}`}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      kg
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Intl.NumberFormat("es-CO", {
                        style: "currency",
                        currency: "COP",
                        minimumFractionDigits: 0,
                      }).format((5 + item * 2) * 1000)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          item === 1
                            ? "bg-red-100 text-red-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {item === 1 ? "Bajo Stock" : "Disponible"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button className="text-purple-600 hover:text-purple-900 transition-colors">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button className="text-red-600 hover:text-red-900 transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Componente Sales
  const Sales = () => {
    const [salesData, setSalesData] = useState<any[] | null>(null);
    const [selectedPeriod, setSelectedPeriod] = useState("monthly");

    async function loadSalesData() {
      try {
        const response = await odooAPI.call("sale.order", "search_read", [[]]);
        if (response.result) {
          setSalesData(response.result);
        }
      } catch (error) {
        console.error("Error loading sales data:", error);
      }
    }

    useEffect(() => {
      loadSalesData();
    }, [selectedPeriod]);

    if (!salesData) {
      return <div>Loading sales data...</div>;
    }

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">
            Gestión de Ventas
          </h2>

          <div className="flex gap-3">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-4 py-2.5 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
            >
              <option value="daily">Diario</option>
              <option value="weekly">Semanal</option>
              <option value="monthly">Mensual</option>
              <option value="yearly">Anual</option>
            </select>
            <button className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-2.5 rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all duration-300 flex items-center gap-2 shadow-lg">
              <Plus className="h-4 w-4" />
              Nueva Venta
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Gráfico de ventas */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Tendencia de Ventas
            </h3>
            <div className="h-80 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl flex items-center justify-center">
              <p className="text-gray-500">Gráfico de tendencia de ventas</p>
            </div>
          </div>

          {/* Resumen de ventas */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Resumen
            </h3>
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-xl">
                <div className="flex items-center justify-between">
                  <span className="text-green-700 font-medium">
                    Ventas Totales
                  </span>
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
                <p className="text-2xl font-bold text-green-900 mt-2">
                  {new Intl.NumberFormat("es-CO", {
                    style: "currency",
                    currency: "COP",
                    minimumFractionDigits: 0,
                  }).format(125430000)}
                </p>
              </div>
              <div className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl">
                <div className="flex items-center justify-between">
                  <span className="text-blue-700 font-medium">Órdenes</span>
                  <ShoppingCart className="h-5 w-5 text-blue-600" />
                </div>
                <p className="text-2xl font-bold text-blue-900 mt-2">1,234</p>
              </div>
              <div className="p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl">
                <div className="flex items-center justify-between">
                  <span className="text-purple-700 font-medium">Clientes</span>
                  <Users className="h-5 w-5 text-purple-600" />
                </div>
                <p className="text-2xl font-bold text-purple-900 mt-2">567</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla de ventas recientes */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-purple-100">
          <div className="p-6 border-b border-purple-100">
            <h3 className="text-lg font-semibold text-gray-900">
              Ventas Recientes
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-purple-50 to-indigo-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Orden
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Estado
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-purple-100">
                {[1, 2, 3, 4, 5].map((item) => (
                  <tr
                    key={item}
                    className="hover:bg-purple-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      #VTA-{1000 + item}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      Cliente {item}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      2025-01-{15 + item}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {new Intl.NumberFormat("es-CO", {
                        style: "currency",
                        currency: "COP",
                        minimumFractionDigits: 0,
                      }).format((123.45 + item * 25) * 1000)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        Completado
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Componente Purchases
  const Purchases = () => {
    const [purchaseData, setPurchaseData] = useState<any[] | null>(null);
    const [selectedTab, setSelectedTab] = useState("orders");

    useEffect(() => {
      loadPurchaseData();
    }, [selectedTab]);

    const loadPurchaseData = async () => {
      try {
        const response = await odooAPI.call("purchase.order", "search_read", [
          [],
        ]);
        if (response.result) {
          setPurchaseData(response.result);
        }
      } catch (error) {
        console.error("Error loading purchase data:", error);
      }
    };

    if (!purchaseData) {
      return <div>Loading purchase data...</div>;
    }
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">
            Gestión de Compras
          </h2>
          <button className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-2.5 rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all duration-300 flex items-center gap-2 shadow-lg">
            <Plus className="h-4 w-4" />
            Nueva Compra
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 border-b border-purple-100">
          {["Órdenes", "Proveedores", "Facturas", "Reportes"].map((tab) => (
            <button
              key={tab}
              onClick={() => setSelectedTab(tab.toLowerCase())}
              className={`pb-3 px-4 border-b-3 transition-all duration-300 font-medium ${
                selectedTab ===
                tab.toLowerCase().replace("ó", "o").replace("í", "i")
                  ? "border-purple-600 text-purple-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* KPIs de compras */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Compras del Mes</p>
                <p className="text-2xl font-bold text-gray-900">
                  {new Intl.NumberFormat("es-CO", {
                    style: "currency",
                    currency: "COP",
                    minimumFractionDigits: 0,
                  }).format(45230000)}
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl">
                <DollarSign className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Órdenes Pendientes</p>
                <p className="text-2xl font-bold text-gray-900">23</p>
              </div>
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-3 rounded-xl">
                <Clock className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Proveedores Activos</p>
                <p className="text-2xl font-bold text-gray-900">12</p>
              </div>
              <div className="bg-gradient-to-br from-green-500 to-green-600 p-3 rounded-xl">
                <Users className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabla de compras */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-purple-100">
          <div className="p-6 border-b border-purple-100">
            <h3 className="text-lg font-semibold text-gray-900">
              Órdenes de Compra
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-purple-50 to-indigo-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Orden
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Proveedor
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Estado
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-purple-100">
                {[1, 2, 3, 4, 5].map((item) => (
                  <tr
                    key={item}
                    className="hover:bg-purple-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      #PO-{2000 + item}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      Proveedor {item}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      2025-01-{10 + item}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {new Intl.NumberFormat("es-CO", {
                        style: "currency",
                        currency: "COP",
                        minimumFractionDigits: 0,
                      }).format((1234.56 + item * 250) * 1000)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          item % 2 === 0
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {item % 2 === 0 ? "Pendiente" : "Recibido"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Componente Accounting
  const Accounting = () => {
    const [, setAccountingData] = useState<any[] | null>([]);
    const [selectedPeriod, setSelectedPeriod] = useState("monthly");

    useEffect(() => {
      loadAccountingData();
    }, [selectedPeriod]);

    const loadAccountingData = async () => {
      try {
        const response = await odooAPI.call("account.move", "search_read", [
          [],
        ]);
        if (response.result) {
          setAccountingData(response.result);
        }
      } catch (error) {
        console.error("Error loading accounting data:", error);
      }
    };

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Contabilidad</h2>
          <div className="flex gap-3">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-4 py-2.5 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
            >
              <option value="monthly">Mensual</option>
              <option value="quarterly">Trimestral</option>
              <option value="yearly">Anual</option>
            </select>
            <button className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-2.5 rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all duration-300 flex items-center gap-2 shadow-lg">
              <FileText className="h-4 w-4" />
              Nuevo Asiento
            </button>
          </div>
        </div>

        {/* Resumen financiero */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Ingresos</p>
                <p className="text-2xl font-bold text-green-600">
                  {new Intl.NumberFormat("es-CO", {
                    style: "currency",
                    currency: "COP",
                    minimumFractionDigits: 0,
                  }).format(234567000)}
                </p>
              </div>
              <div className="bg-gradient-to-br from-green-500 to-green-600 p-3 rounded-xl">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Gastos</p>
                <p className="text-2xl font-bold text-red-600">
                  {new Intl.NumberFormat("es-CO", {
                    style: "currency",
                    currency: "COP",
                    minimumFractionDigits: 0,
                  }).format(89123000)}
                </p>
              </div>
              <div className="bg-gradient-to-br from-red-500 to-red-600 p-3 rounded-xl">
                <DollarSign className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Utilidad</p>
                <p className="text-2xl font-bold text-purple-600">
                  {new Intl.NumberFormat("es-CO", {
                    style: "currency",
                    currency: "COP",
                    minimumFractionDigits: 0,
                  }).format(145444000)}
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-3 rounded-xl">
                <Calculator className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Margen</p>
                <p className="text-2xl font-bold text-indigo-600">62%</p>
              </div>
              <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 p-3 rounded-xl">
                <BarChart3 className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Gráfico de flujo de caja */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Flujo de Caja
          </h3>
          <div className="h-80 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl flex items-center justify-center">
            <p className="text-gray-500">Gráfico de flujo de caja</p>
          </div>
        </div>

        {/* Tabla de asientos contables */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-purple-100">
          <div className="p-6 border-b border-purple-100">
            <h3 className="text-lg font-semibold text-gray-900">
              Asientos Contables Recientes
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-purple-50 to-indigo-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Número
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Concepto
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Debe
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Haber
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-purple-100">
                {[1, 2, 3, 4, 5].map((item) => (
                  <tr
                    key={item}
                    className="hover:bg-purple-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      AST-{3000 + item}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      2025-01-{20 + item}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      Venta de productos
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Intl.NumberFormat("es-CO", {
                        style: "currency",
                        currency: "COP",
                        minimumFractionDigits: 0,
                      }).format((1500 + item * 100) * 1000)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Intl.NumberFormat("es-CO", {
                        style: "currency",
                        currency: "COP",
                        minimumFractionDigits: 0,
                      }).format((1500 + item * 100) * 1000)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Componente Employees
  const Employees = () => {
    const [, setEmployeeData] = useState<any[] | null>([]);
    const [selectedDepartment, setSelectedDepartment] = useState("all");

    useEffect(() => {
      loadEmployeeData();
    }, [selectedDepartment]);

    const loadEmployeeData = async () => {
      try {
        const response = await odooAPI.call("hr.employee", "search_read", [[]]);
        if (response.result) {
          setEmployeeData(response.result);
        }
      } catch (error) {
        console.error("Error loading employee data:", error);
      }
    };

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">
            Gestión de Empleados
          </h2>
          <div className="flex gap-3">
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="px-4 py-2.5 border border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
            >
              <option value="all">Todos los departamentos</option>
              <option value="kitchen">Cocina</option>
              <option value="service">Servicio</option>
              <option value="admin">Administración</option>
            </select>
            <button className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-2.5 rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all duration-300 flex items-center gap-2 shadow-lg">
              <Plus className="h-4 w-4" />
              Nuevo Empleado
            </button>
          </div>
        </div>

        {/* Estadísticas de empleados */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Empleados</p>
                <p className="text-2xl font-bold text-purple-600">45</p>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-3 rounded-xl">
                <Users className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Presentes Hoy</p>
                <p className="text-2xl font-bold text-green-600">38</p>
              </div>
              <div className="bg-gradient-to-br from-green-500 to-green-600 p-3 rounded-xl">
                <Check className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Ausentes</p>
                <p className="text-2xl font-bold text-red-600">7</p>
              </div>
              <div className="bg-gradient-to-br from-red-500 to-red-600 p-3 rounded-xl">
                <X className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Nuevos (Mes)</p>
                <p className="text-2xl font-bold text-blue-600">3</p>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl">
                <Plus className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabla de empleados */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-purple-100">
          <div className="p-6 border-b border-purple-100">
            <h3 className="text-lg font-semibold text-gray-900">
              Lista de Empleados
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-purple-50 to-indigo-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Empleado
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Departamento
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Cargo
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Salario
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-purple-100">
                {[1, 2, 3, 4, 5].map((item) => (
                  <tr
                    key={item}
                    className="hover:bg-purple-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 bg-gradient-to-br from-purple-200 to-indigo-200 rounded-full mr-3 flex items-center justify-center">
                          <User className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            Empleado {item}
                          </div>
                          <div className="text-sm text-gray-500">
                            empleado{item}@byte.com
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item % 3 === 0
                        ? "Cocina"
                        : item % 2 === 0
                          ? "Servicio"
                          : "Administración"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item % 3 === 0
                        ? "Chef"
                        : item % 2 === 0
                          ? "Mesero"
                          : "Supervisor"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Intl.NumberFormat("es-CO", {
                        style: "currency",
                        currency: "COP",
                        minimumFractionDigits: 0,
                      }).format((1200 + item * 200) * 1000)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          item % 5 === 0
                            ? "bg-red-100 text-red-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {item % 5 === 0 ? "Ausente" : "Presente"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button className="text-purple-600 hover:text-purple-900 transition-colors">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button className="text-blue-600 hover:text-blue-900 transition-colors">
                          <Eye className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Componente Website
  const Website = () => {
    const [websiteStats] = useState({
      visitors: 12543,
      pageViews: 45678,
      orders: 234,
      conversion: 1.86,
    });

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">
            Gestión de Página Web
          </h2>
          <div className="flex gap-3">
            <button className="bg-white border border-purple-200 px-5 py-2.5 rounded-xl hover:bg-purple-50 transition-colors flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Vista Previa
            </button>
            <button className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-2.5 rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all duration-300 flex items-center gap-2 shadow-lg">
              <Globe className="h-4 w-4" />
              Actualizar Sitio
            </button>
          </div>
        </div>

        {/* Estadísticas web */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Visitantes</p>
                <p className="text-2xl font-bold text-purple-600">
                  {websiteStats.visitors.toLocaleString()}
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-3 rounded-xl">
                <Users className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Páginas Vistas</p>
                <p className="text-2xl font-bold text-blue-600">
                  {websiteStats.pageViews.toLocaleString()}
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl">
                <Eye className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Órdenes Online</p>
                <p className="text-2xl font-bold text-green-600">
                  {websiteStats.orders}
                </p>
              </div>
              <div className="bg-gradient-to-br from-green-500 to-green-600 p-3 rounded-xl">
                <ShoppingCart className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Conversión</p>
                <p className="text-2xl font-bold text-orange-600">
                  {websiteStats.conversion}%
                </p>
              </div>
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-3 rounded-xl">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Gestión de contenido */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Páginas Principales
            </h3>
            <div className="space-y-3">
              {[
                { name: "Inicio", status: "Activo", views: "12.5K" },
                { name: "Menú", status: "Activo", views: "8.3K" },
                { name: "Reservas", status: "Activo", views: "4.2K" },
                { name: "Contacto", status: "Activo", views: "2.1K" },
              ].map((page, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 hover:bg-purple-50 rounded-xl transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-lg flex items-center justify-center">
                      <Globe className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {page.name}
                      </p>
                      <p className="text-xs text-gray-500">{page.status}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {page.views}
                    </p>
                    <p className="text-xs text-gray-500">vistas</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Configuración SEO
            </h3>
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Check className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium text-green-800">
                    Meta Títulos
                  </span>
                </div>
                <p className="text-xs text-green-700">
                  Configurados correctamente
                </p>
              </div>

              <div className="p-4 bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-800">
                    Descripciones
                  </span>
                </div>
                <p className="text-xs text-yellow-700">
                  Algunas páginas sin descripción
                </p>
              </div>

              <div className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Settings className="h-5 w-5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">
                    Velocidad
                  </span>
                </div>
                <p className="text-xs text-blue-700">
                  Optimización recomendada
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Componente Email Marketing
  const EmailMarketing = () => {
    const [emailStats] = useState({
      sent: 2450,
      delivered: 2398,
      opened: 1259,
      clicked: 287,
    });

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Email Marketing</h2>
          <div className="flex gap-3">
            <button className="bg-white border border-purple-200 px-5 py-2.5 rounded-xl hover:bg-purple-50 transition-colors flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Plantillas
            </button>
            <button className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-2.5 rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all duration-300 flex items-center gap-2 shadow-lg">
              <Mail className="h-4 w-4" />
              Nueva Campaña
            </button>
          </div>
        </div>

        {/* Estadísticas de email */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Enviados</p>
                <p className="text-2xl font-bold text-purple-600">
                  {emailStats.sent.toLocaleString()}
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-3 rounded-xl">
                <Mail className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Entregados</p>
                <p className="text-2xl font-bold text-blue-600">
                  {emailStats.delivered.toLocaleString()}
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 rounded-xl">
                <Check className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Abiertos</p>
                <p className="text-2xl font-bold text-green-600">
                  {emailStats.opened.toLocaleString()}
                </p>
              </div>
              <div className="bg-gradient-to-br from-green-500 to-green-600 p-3 rounded-xl">
                <Eye className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Clicks</p>
                <p className="text-2xl font-bold text-orange-600">
                  {emailStats.clicked.toLocaleString()}
                </p>
              </div>
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-3 rounded-xl">
                <Search className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Campañas recientes */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-purple-100">
          <div className="p-6 border-b border-purple-100">
            <h3 className="text-lg font-semibold text-gray-900">
              Campañas Recientes
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-purple-50 to-indigo-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Campaña
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Enviados
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Abiertos
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Clicks
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Estado
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-purple-100">
                {[
                  {
                    name: "Promoción de Fin de Semana",
                    date: "2025-01-15",
                    sent: 500,
                    opened: 245,
                    clicked: 67,
                  },
                  {
                    name: "Nuevo Menú de Temporada",
                    date: "2025-01-12",
                    sent: 450,
                    opened: 198,
                    clicked: 42,
                  },
                  {
                    name: "Descuento para Clientes VIP",
                    date: "2025-01-10",
                    sent: 200,
                    opened: 156,
                    clicked: 89,
                  },
                  {
                    name: "Newsletter Mensual",
                    date: "2025-01-08",
                    sent: 800,
                    opened: 423,
                    clicked: 78,
                  },
                ].map((campaign, idx) => (
                  <tr
                    key={idx}
                    className="hover:bg-purple-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {campaign.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {campaign.date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {campaign.sent}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {campaign.opened} (
                      {Math.round((campaign.opened / campaign.sent) * 100)}%)
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {campaign.clicked} (
                      {Math.round((campaign.clicked / campaign.sent) * 100)}%)
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        Completado
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Renderizar módulo activo
  const renderModule = () => {
    switch (activeModule) {
      case "dashboard":
        return <Dashboard />;
      case "menu-digital":
        return <MenuDigital />;
      case "pos":
        return <POS />;
      case "inventory":
        return <Inventory />;
      case "sales":
        return <Sales />;
      case "purchases":
        return <Purchases />;
      case "accounting":
        return <Accounting />;
      case "employees":
        return <Employees />;
      case "website":
        return <Website />;
      case "email":
        return <EmailMarketing />;
      default:
        return (
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-purple-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Módulo: {modules.find((m) => m.id === activeModule)?.name}
            </h2>
            <p className="text-gray-600">
              Este módulo está en desarrollo y será conectado con Odoo 18
              Community Edition.
            </p>
            <div className="mt-6 p-4 bg-purple-50 rounded-xl">
              <p className="text-sm text-purple-700">
                <strong>API Endpoint:</strong> {ODOO_API_CONFIG.baseURL}/api/
                {activeModule}
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="h-screen bg-gray-50 flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar Moderno */}
      <aside
        className={`
          bg-gradient-to-b from-purple-900 via-purple-800 to-indigo-900 text-white 
          transition-all duration-300 flex flex-col justify-between
          ${sidebarOpen ? "w-64" : "w-0 md:w-24"}
          fixed md:static top-0 left-0 h-full z-50 md:z-10
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
          overflow-hidden
        `}
      >
        {/* Patrón decorativo */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full -translate-x-20 -translate-y-20"></div>
          <div className="absolute bottom-0 right-0 w-60 h-60 bg-white rounded-full translate-x-20 translate-y-20"></div>
        </div>

        {/* Contenido del sidebar */}
        <div className="relative z-10 h-full flex flex-col">
          {/* Header del sidebar - solo para expandido */}
          {sidebarOpen && (
            <div className="p-4 flex items-center justify-between border-b border-white border-opacity-10">
              <div>
                <h1 className="font-bold text-2xl bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
                  Byte
                </h1>
                <p className="text-xs text-purple-300 mt-1">
                  Powered by Sentral-AI
                </p>
              </div>

              {/* Botón menú */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors"
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>
          )}

          {/* Navegación */}
          <nav
            className={`flex-1 py-4 ${sidebarOpen ? "px-3 overflow-y-auto" : "px-1"}`}
          >
            {modules.map((module) => {
              const Icon = module.icon;
              const isActive = activeModule === module.id;
              return (
                <div
                  key={module.id}
                  className={`relative group ${sidebarOpen ? "mb-2" : "mb-4"} ${!sidebarOpen ? "flex justify-center" : ""}`}
                >
                  <button
                    onClick={() => {
                      setActiveModule(module.id);
                      // Auto cerrar en mobile
                      if (window.innerWidth < 768) setSidebarOpen(false);
                    }}
                    className={`flex items-center rounded-xl transition-all duration-300 group
                      ${
                        !sidebarOpen
                          ? "justify-center md:w-16 md:h-16 md:p-4"
                          : "w-full gap-3 px-3 py-2.5"
                      }
                      ${
                        isActive
                          ? "bg-white bg-opacity-20 backdrop-blur-sm shadow-lg"
                          : "hover:bg-white hover:bg-opacity-10"
                      }
                    `}
                  >
                    {!sidebarOpen ? (
                      /* Icono simple para sidebar colapsado */
                      <Icon className="h-5 w-5" />
                    ) : (
                      /* Layout completo para sidebar expandido */
                      <>
                        <div
                          className={`p-2 rounded-lg transition-all duration-300 flex-shrink-0
                            ${
                              isActive
                                ? "bg-white bg-opacity-20"
                                : "bg-white bg-opacity-10 group-hover:bg-opacity-20"
                            }
                          `}
                        >
                          <Icon className="h-4 w-4" />
                        </div>

                        <span className="font-medium transition-all duration-300 whitespace-nowrap">
                          {module.name}
                        </span>

                        {isActive && (
                          <ChevronRight className="h-4 w-4 ml-auto flex-shrink-0" />
                        )}
                      </>
                    )}
                  </button>

                  {/* Tooltip para sidebar colapsado */}
                  {!sidebarOpen && (
                    <div className="absolute left-full ml-3 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 hidden md:block">
                      {module.name}
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-2 h-2 bg-gray-900 rotate-45"></div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* User section - solo para expandido */}
          {sidebarOpen && (
            <div className="border-t border-white border-opacity-10 p-3">
              <div className="space-y-2">
                <div className="relative group">
                  <button className="flex items-center w-full gap-3 px-3 py-2.5 rounded-xl hover:bg-white hover:bg-opacity-10 transition-all duration-300">
                    <Settings className="h-4 w-4 flex-shrink-0" />
                    <span>Configuración</span>
                  </button>
                </div>

                <div className="relative group">
                  <button className="flex items-center w-full gap-3 px-3 py-2.5 rounded-xl hover:bg-white hover:bg-opacity-10 transition-all duration-300 text-red-300 hover:text-red-200">
                    <LogOut className="h-4 w-4 flex-shrink-0" />
                    <span>Cerrar Sesión</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Botón menú para sidebar colapsado */}
          {!sidebarOpen && (
            <div className="hidden md:block absolute top-4 right-4 z-20">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors"
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile Header con botón de menú */}
        <div className="md:hidden bg-white border-b border-purple-100 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-purple-50 rounded-lg transition-colors"
          >
            <Menu className="h-5 w-5 text-gray-600" />
          </button>
          <h1 className="font-bold text-xl text-purple-600">Byte</h1>
          <div className="w-9"></div> {/* Spacer para centrar el título */}
        </div>

        {/* Header Desktop */}
        <header className="hidden md:block bg-white shadow-sm border-b border-purple-100">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {modules.find((m) => m.id === activeModule)?.name}
              </h2>
            </div>

            <div className="flex items-center gap-4">
              <button className="relative p-2 hover:bg-purple-50 rounded-lg transition-colors">
                <Bell className="h-5 w-5 text-gray-600" />
                {notifications > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 bg-purple-600 text-white text-xs rounded-full flex items-center justify-center">
                    {notifications}
                  </span>
                )}
              </button>

              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">
                    {user.name}
                  </p>
                  <p className="text-xs text-gray-500">{user.role}</p>
                </div>
                <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-white" />
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gradient-to-br from-purple-50 via-white to-indigo-50">
          {renderModule()}
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-purple-100 px-4 md:px-6 py-3">
          <div className="flex items-center justify-between text-xs md:text-sm text-gray-500">
            <span className="hidden sm:block">
              © 2025 Byte Restaurant System
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              Conectado v {ODOO_API_CONFIG.version}
            </span>
            <span className="hidden sm:block">
              Powered by{" "}
              <span className="font-semibold text-purple-600">Sentral-AI</span>
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ByteRestaurantSystem;
